import { useConfigStore } from '@/stores';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SwiperRef } from 'swiper/react';

export const useVerticalFloorManagement = (floorsParam: IDataFloor[], amountOfFloorsToShow = 5, selectedFloorName?: string) => {
    const swiperRef = useRef<SwiperRef>(null);
    const [isDisabledPrev, setIsDisabledPrev] = useState(false);
    const [isDisabledNext, setIsDisabledNext] = useState(false);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);

    const floors = useMemo(() => {
        if (!Array.isArray(floorsParam) || !floorsParam?.length) return [];
        return floorsParam;
    }, [floorsParam]);

    const selectedFloorIndex = useMemo(() => {
        if (!selectedFloorName || !floors.length) return -1;
        const index = floors.findIndex((floor) => Array.isArray(floor.name) && floor.name?.length && floor.name[0]?.text === selectedFloorName);
        return index >= 0 ? index : -1;
    }, [selectedFloorName, floors]);

    const shouldScroll = useMemo(() => floors.length > amountOfFloorsToShow, [floors, amountOfFloorsToShow]);

    const handleDisableNext = useCallback((disabled: boolean) => {
        setIsDisabledNext(disabled);
    }, []);

    const handleDisablePrev = useCallback((disabled: boolean) => {
        setIsDisabledPrev(disabled);
    }, []);

    const handleScrollUp = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slidePrev?.();
    }, []);

    const handleScrollDown = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slideNext?.();
    }, []);

    const scrollToSelectedFloor = useCallback(() => {
        if (selectedFloorIndex < 0 || !swiperRef.current?.swiper) {
            return;
        }

        swiperRef.current.swiper.slideTo(selectedFloorIndex, 0);
        handleDisablePrev(swiperRef.current.swiper.isBeginning);
        handleDisableNext(swiperRef.current.swiper.isEnd);
    }, [handleDisableNext, handleDisablePrev, selectedFloorIndex]);

    useEffect(() => {
        scrollToSelectedFloor();
    }, [scrollToSelectedFloor, screenMode]);

    return {
        isDisabledPrev,
        isDisabledNext,
        shouldScroll,
        swiperRef,
        handleScrollUp,
        handleScrollDown,
        handleDisableNext,
        handleDisablePrev,
        scrollToSelectedFloor,
    };
};
